// Recuperación cliente ante "stale chunk after deploy" (decisiones §37, §52).
//
// El SPA hace code-splitting: cada página lazy es un chunk hasheado en /assets/.
// Al desplegar, el container `frontend` se recrea con un dist nuevo (hashes
// nuevos) y los chunks del build anterior desaparecen → 404 duro. Una pestaña
// ya abierta (bundle viejo en memoria) que navega a una ruta lazy tras el
// deploy dispara un import() que rechaza → antes: página de error + Sentry.
//
// Estrategia: al detectar un fallo de carga de chunk, recargar UNA vez. El
// index.html (no-cache) trae los hashes nuevos, así que el segundo intento
// resuelve y el usuario aterriza en la ruta que pedía. Con guarda anti-bucle
// para no recargar en loop si el deploy está genuinamente roto.
//
// Helper PURO (sin Context ni imports pesados): se usa desde main.jsx y desde
// ErrorFallback.jsx, que vive fuera de todos los providers.

const RELOAD_TS_KEY = 'chunk-reload-ts';
const RELOAD_WINDOW_MS = 10_000;

// Variantes de mensaje según navegador (Chrome/Firefox/Safari) + webpack-style.
const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk [\d]+ failed|Failed to load module script/i;

/**
 * ¿El error es un fallo de carga de chunk (stale chunk tras deploy)?
 * @param {unknown} err
 * @returns {boolean}
 */
export function isChunkLoadError(err) {
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  const msg = typeof err === 'string' ? err : (err.message || '');
  return CHUNK_ERROR_RE.test(msg);
}

// Congelado UNA vez al cargar el módulo, ANTES de que esta sesión de página
// dispare ningún reload. Refleja si LLEGAMOS aquí venidos de un reload previo
// por chunk (marca puesta en la carga anterior), no la marca que pondremos
// nosotros al recargar. Es lo que distingue 1er fallo (transitorio) de 2º
// fallo (persistente = deploy roto de verdad).
let cameFromReload = false;
try {
  const last = Number(sessionStorage.getItem(RELOAD_TS_KEY) || 0);
  cameFromReload = Date.now() - last < RELOAD_WINDOW_MS;
} catch {
  // sessionStorage no disponible (modo privado estricto, etc.) → tratamos
  // como "no veníamos de reload".
  cameFromReload = false;
}

/**
 * ¿Este chunk-error es persistente? True cuando ya recargamos hace poco y
 * volvió a fallar → hay que reportarlo a Sentry (no es una carrera de deploy).
 * @returns {boolean}
 */
export function isPersistentChunkFailure() {
  return cameFromReload;
}

/**
 * Intenta recuperar recargando UNA vez. Devuelve true si disparó el reload.
 * Si ya veníamos de un reload reciente (fallo persistente), NO recarga otra
 * vez para evitar el bucle infinito.
 * @returns {boolean}
 */
export function attemptChunkReload() {
  if (cameFromReload) return false;
  try {
    sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()));
  } catch {
    // Sin sessionStorage no podemos garantizar la guarda anti-bucle: mejor no
    // recargar automáticamente (que caiga al ErrorFallback) que arriesgar loop.
    return false;
  }
  window.location.reload();
  return true;
}
